import * as _ from "lodash";
import { TaskQueue } from "../../../../common/taskQueue";
import { Utils } from "../../../../common/utils";
import { WIDGET_DIMENSIONS } from "../../../../common/widgetLayout";
import { Gauge } from "../../../../lib/gauge";
import { DashboardChannel } from "../../../../models/DashboardChannel";
import { BaseDashboardWidgetViewOptions } from "../_BaseWidgetView";
import { BaseSimpleWidget } from "../simplewidget/SimpleWidget";

interface ValueCategory {
    limit: [number, number], // The lower (including) and upper (excluding) bounds of the category
    type: string
}

export class IAQWidget extends BaseSimpleWidget {

    private channelsTemplate = _.template(require('templates/dashboard/widgets/iaq/iaq.html'));
    private advancedConfig = false;
    private useCustomTitle = false;
    protected useCustomLabels = false;
    
    // Collection of gauges by dashboard channel id
    private gaugesByDashChannelId = new Map();

    private firstRender = true;
    private triggeredBySidepanel = false;

    private EpaAQICategories = {
        'excellent': {
            status: 'excellent',
            color: 'green',
            aqiLimits: []
        },
        'good': {
            status: 'good',
            color: 'green',
            aqiLimits: [0, 51]
        },
        'moderate': {
            status: 'moderate',
            color: 'yellow',
            aqiLimits: [51, 101]
        },
        'poor': {
            status: 'poor',
            color: 'orange',
            aqiLimits: []
        },
        'usg': {
            status: 'usg',
            color: 'orange',
            aqiLimits: [101, 151]
        },
        'unhealthy': {
            status: 'unhealthy',
            color: 'red',
            aqiLimits: [151, 201]
        },
        'very-unhealthy': {
            status: 'very unhealthy',
            color: 'purple',
            aqiLimits: [201, 251]
        },
        'hazardous': {
            status: 'hazardous',
            color: 'maroon',
            aqiLimits: [251, 301]
        },
    };
    private CO2Categories = _.extend(this.EpaAQICategories, {
        'outdoor': {
            status: 'fresh air',
            color: 'green'
        }
    });
    private HumidityCategories = {
        'crit-low': {
            status: 'too dry',
            color: 'red'
        },
        'warn-low': {
            status: 'dry',
            color: 'yellow'
        },
        'good': {
            status: 'comfortable',
            color: 'green'
        },
        'warn-high': {
            status: 'humid',
            color: 'yellow'
        },
        'crit-high': {
            status: 'too humid',
            color: 'red'
        },
    };
    private TemperatureCategories = {
        'crit-low': {
            status: 'cold',
            color: 'blue'
        },
        'warn-low': {
            status: 'cool',
            color: 'light-blue'
        },
        'good': {
            status: 'comfortable',
            color: 'green'
        },
        'warn-high': {
            status: 'warm',
            color: 'yellow'
        },
        'crit-high': {
            status: 'hot',
            color: 'red'
        },
    };

    constructor(options: BaseDashboardWidgetViewOptions) {
        super(_.merge({
            className: 'widget widget-h-2 iaq-widget'
        }, options))
    }

    renderChannels() {
        const rawAdvancedConfig = this.options.widget.getParamValue('ADVANCED_CONFIG', false);
        // coerce the raw value -- which may be a boolean, number, or string -- into a boolean
        this.advancedConfig = !!Number(rawAdvancedConfig);

        const rawUseCustomLabelsConfig = this.options.widget.getParamValue('USE_CUSTOM_LABELS', false);
        // coerce the raw value -- which may be a boolean, number, or string -- into a boolean
        this.useCustomLabels = !!Number(rawUseCustomLabelsConfig);

        const rawUseCustomTitleConfig = this.options.widget.getParamValue('USE_CUSTOM_TITLE', false);
        // coerce the raw value -- which may be a boolean, number, or string -- into a boolean
        this.useCustomTitle = !!Number(rawUseCustomTitleConfig);

        const widgetChannels = this.options.dashboardChannelCollection.where({ dashboard_widget_id: this.options.widgetId });
        const moduleLocation  = widgetChannels[0] ? widgetChannels[0].get('module_location') : "";

        // For EPA AQI categories see https://www3.epa.gov/airnow/aqi-technical-assistance-document-may2016.pdf
        var columns = [];
        // Check if there is a temperature channel
        var temp = this.getChannel(1);
        if (temp) {
            var value = temp.get('channel_converted_value');
            var isCelcius = temp.get('channel_unit').toLowerCase() === '°c' || temp.get('channel_unit').toLowerCase() === 'c';
            var tempValues = {
                critLow: isCelcius ? 18 : 66,
                warningLow: isCelcius ? 20 : 68,
                warningHigh: isCelcius ? 23 : 74,
                critHigh: isCelcius ? 25 : 77,
            }
            // For Temperature & Humidity the values are based on the limits the user set
            var valueCategory = this.getMatchingValueCategory([
                {
                    limit: [-Infinity, tempValues.critLow],
                    type: 'crit-low'
                },
                {
                    limit: [tempValues.critLow, tempValues.warningLow],
                    type: 'warn-low'
                },
                {
                    limit: [tempValues.warningLow, tempValues.warningHigh],
                    type: 'good'
                },
                {
                    limit: [tempValues.warningHigh, tempValues.critHigh],
                    type: 'warn-high'
                },
                {
                    limit: [tempValues.critHigh, Infinity],
                    type: 'crit-high'
                },
            ], value, true);
            var type = valueCategory ? valueCategory.type : 'good';
            var state = this.advancedConfig ? this.getStateFromRange(temp) : this.getStateForRegularThresholds('temp', type, value);

            let fontSize = 100;
            if(this.useCustomLabels && temp.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: temp.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? temp.get('dashboard_channel_name') : temp.get('channel_name'),
                abbr: this.useCustomLabels ? temp.get('dashboard_channel_name') : 'Temperature',
                fontSize: fontSize,
                value: this.getFormattedValue(temp),
                maxValue: Math.max(value, 100),
                unit: temp.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('temp', type),
                channelLastTimestamp: temp.get('channel_last_timestamp')
            });
        }

        // Check if there is a humidity channel
        var hum = this.getChannel(2);
        if (hum) {
            var value = hum.get('channel_converted_value');
            var humValues = {
                critLow: 20,
                warningLow: 30,
                warningHigh: 70,
                critHigh: 80,
            }
            // For Temperature & Humidity the values are based on the limits the user set
            var valueCategory = this.getMatchingValueCategory([
                { limit: [-Infinity, humValues.critLow], type: 'crit-low' },
                {
                    limit: [humValues.critLow, humValues.warningLow],
                    type: 'warn-low'
                },
                {
                    limit: [humValues.warningLow, humValues.warningHigh],
                    type: 'good'
                },
                {
                    limit: [humValues.warningHigh, humValues.critHigh],
                    type: 'warn-high'
                },
                { limit: [humValues.critHigh, Infinity], type: 'crit-high' },
            ], value, true);
            var type = valueCategory ? valueCategory.type : 'good';
            var state = this.advancedConfig ? this.getStateFromRange(hum) : this.getStateForRegularThresholds('hum', type, value);

            let fontSize = 100;
            if(this.useCustomLabels && hum.get('dashboard_channel_name').length > 12 ) fontSize = 70;

            columns.push({
                id: hum.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? hum.get('dashboard_channel_name') : hum.get('channel_name'),
                abbr: this.useCustomLabels ? hum.get('dashboard_channel_name') : 'Humidity',
                fontSize: fontSize,
                value: this.getFormattedValue(hum),
                maxValue: 100,
                unit: hum.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('hum', type),
                channelLastTimestamp: hum.get('channel_last_timestamp')
            });
        }

        // Check if there is a CO2 channel
        var co2 = this.getChannel(3);
        if (co2) {
            var value = co2.get('channel_converted_value');
            // There are no EPA recommendations for CO2.
            // The following limits are estimated based on https://www.kane.co.uk/knowledge-centre/what-are-safe-levels-of-co-and-co2-in-rooms
            // The following limits are in ppm
            var valueCategory = this.getMatchingValueCategory([
                { limit: [-Infinity, 351], type: 'outdoor' },
                { limit: [351, 1001], type: 'good' },
                { limit: [1001, 2001], type: 'moderate' },
                { limit: [2001, 5001], type: 'unhealthy' },
                { limit: [5001, 40000], type: 'very-unhealthy' },
                { limit: [40000, Infinity], type: 'hazardous' },
            ], value, true);
            var state = this.advancedConfig ? this.getStateFromRange(co2) : this.getStateForRegularThresholds('co2', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && co2.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: co2.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? co2.get('dashboard_channel_name') : co2.get('channel_name'),
                abbr: this.useCustomLabels ? co2.get('dashboard_channel_name') : 'CO<sub>2</sub>',
                fontSize: fontSize,
                value: this.getFormattedValue(co2),
                maxValue: Math.max(value, 2001),
                unit: co2.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('co2', valueCategory.type),
                channelLastTimestamp: co2.get('channel_last_timestamp')
            });
        }

        // Check if there is a VOC channel
        var voc = this.getChannel(4);
        if (voc) {
            var value = voc.get('channel_converted_value');
            // There are no EPA recommendations for VOC.
            // The following limits are estimated based on international standards detailed here:
            // https://www.sensirion.com/fileadmin/user_upload/customers/sensirion/Dokumente/9_Gas_Sensors/Sensirion_Gas_Sensors_IAQ-VOC_Product_Brochure_EN.pdf
            // The following limits are in ppb
            var valueCategory = this.getMatchingValueCategory([
                { limit: [-Infinity, 66], type: 'excellent' },
                { limit: [66, 221], type: 'good' },
                { limit: [221, 661], type: 'moderate' },
                { limit: [661, 2201], type: 'poor' },
                { limit: [2201, Infinity], type: 'unhealthy' },
            ], value, true);
            var state = this.advancedConfig ? this.getStateFromRange(voc) : this.getStateForRegularThresholds('voc', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && voc.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: voc.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? voc.get('dashboard_channel_name') : voc.get('channel_name'),
                abbr: this.useCustomLabels ? voc.get('dashboard_channel_name') : 'VOC',
                fontSize: fontSize,
                value: this.getFormattedValue(voc),
                maxValue: Math.max(value, 661),
                unit: voc.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('voc', valueCategory.type),
                channelLastTimestamp: voc.get('channel_last_timestamp')
            });
        }

        // Check if there is a PM1.0 channel
        var pm1 = this.getChannel(6);
        if (pm1) {
            var value = pm1.get('channel_converted_value');
            // The following limits are in ug/m^3
            var valueCategory = this.getMatchingValueCategory([
                { limit: [0, 12.1], type: 'good' },
                { limit: [12.1, 35.5], type: 'moderate' },
                { limit: [35.5, 55.5], type: 'usg' },
                { limit: [55.5, 150.5], type: 'unhealthy' },
                { limit: [150.5, 250.5], type: 'very-unhealthy' },
                { limit: [250.5, 500.5], type: 'hazardous' },
            ], value, true);
            var aqi = this.getAirQualityIndex(
                this.EpaAQICategories[valueCategory.type].aqiLimits,
                valueCategory.limit,
                value
            );
            var state = this.advancedConfig ? this.getStateFromRange(pm1) : this.getStateForRegularThresholds('pm', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && pm1.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: pm1.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? pm1.get('dashboard_channel_name') : pm1.get('channel_name'),
                abbr: this.useCustomLabels ? pm1.get('dashboard_channel_name') : 'PM<sub>1.0</sub>',
                fontSize: fontSize,
                value: this.getFormattedValue(pm1),
                maxValue: Math.max(value, 55.5),
                unit: pm1.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('pm', valueCategory.type, aqi),
                channelLastTimestamp: pm1.get('channel_last_timestamp')
            });
        }

        // Check if there is a PM2.5 channel
        var pm25 = this.getChannel(7);
        if (pm25) {
            var value = pm25.get('channel_converted_value');
            // The following limits are in ug/m^3
            var valueCategory = this.getMatchingValueCategory([
                { limit: [0, 12.1], type: 'good' },
                { limit: [12.1, 35.5], type: 'moderate' },
                { limit: [35.5, 55.5], type: 'usg' },
                { limit: [55.5, 150.5], type: 'unhealthy' },
                { limit: [150.5, 250.5], type: 'very-unhealthy' },
                { limit: [250.5, 500.5], type: 'hazardous' },
            ], value, true);
            var aqi = this.getAirQualityIndex(
                this.EpaAQICategories[valueCategory.type].aqiLimits,
                valueCategory.limit,
                value
            );
            var state = this.advancedConfig ? this.getStateFromRange(pm25) : this.getStateForRegularThresholds('pm', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && pm25.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: pm25.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? pm25.get('dashboard_channel_name') : pm25.get('channel_name'),
                abbr: this.useCustomLabels ? pm25.get('dashboard_channel_name') : 'PM<sub>2.5</sub>',
                fontSize: fontSize,
                value: this.getFormattedValue(pm25),
                maxValue: Math.max(value, 55.5),
                unit: pm25.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('pm', valueCategory.type, aqi),
                channelLastTimestamp: pm25.get('channel_last_timestamp')
            });
        }

        // Check if there is a PM10 channel
        var pm10 = this.getChannel(8);
        if (pm10) {
            var value = pm10.get('channel_converted_value');
            // The following limits are in ug/m^3
            var valueCategory = this.getMatchingValueCategory([
                { limit: [0, 54], type: 'good' },
                { limit: [54, 155], type: 'moderate' },
                { limit: [155, 255], type: 'usg' },
                { limit: [255, 355], type: 'unhealthy' },
                { limit: [355, 425], type: 'very-unhealthy' },
                { limit: [425, 605], type: 'hazardous' },
            ], value, true);
            var aqi = this.getAirQualityIndex(
                this.EpaAQICategories[valueCategory.type].aqiLimits,
                valueCategory.limit,
                value
            );
            var state = this.advancedConfig ? this.getStateFromRange(pm10) : this.getStateForRegularThresholds('pm', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && pm10.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: pm10.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? pm10.get('dashboard_channel_name') : pm10.get('channel_name'),
                abbr: this.useCustomLabels ? pm10.get('dashboard_channel_name') : 'PM<sub>10</sub>',
                fontSize: fontSize,
                value: this.getFormattedValue(pm10),
                maxValue: Math.max(value, 255),
                unit: pm10.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('pm', valueCategory.type, aqi),
                channelLastTimestamp: pm10.get('channel_last_timestamp')
            });
        }

        // Check if there is a HCHO channel
        var hcho = this.getChannel(99);
        if (hcho) {
            // The following limits come from https://www.osha.gov/OshDoc/data_General_Facts/formaldehyde-factsheet.pdf
            // converted to ppm via https://www.ncbi.nlm.nih.gov/books/NBK138711/
            var value = hcho.get('channel_converted_value');
            // The following limits are in ug/m^3
            var valueCategory = this.getMatchingValueCategory([
                { limit: [-Infinity, Infinity], type: 'good' },
            ], value, true);
            var state = this.advancedConfig ? this.getStateFromRange(hcho) : this.getStateForRegularThresholds('hcho', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && hcho.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: hcho.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? hcho.get('dashboard_channel_name') : hcho.get('channel_name'),
                abbr: this.useCustomLabels ? hcho.get('dashboard_channel_name') : 'HCHO',
                fontSize: fontSize,
                value: this.getFormattedValue(hcho),
                maxValue: Math.max(value, 0.1 * 0.8),
                unit: hcho.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('hcho', valueCategory.type),
                channelLastTimestamp: hcho.get('channel_last_timestamp')
            });
        }

        // Check if there is a NO2 channel
        var no2 = this.getChannel(100);
        if (no2) {
            var value = no2.get('channel_converted_value');
            // The following limits are in ppb
            var valueCategory = this.getMatchingValueCategory([
                { limit: [-Infinity, Infinity], type: 'good' },
            ], value, true);
            var aqi = this.getAirQualityIndex(
                this.EpaAQICategories[valueCategory.type].aqiLimits,
                valueCategory.limit,
                value
            );
            var state = this.advancedConfig ? this.getStateFromRange(no2) : this.getStateForRegularThresholds('no2', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && no2.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: no2.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? no2.get('dashboard_channel_name') : no2.get('channel_name'),
                abbr: this.useCustomLabels ? no2.get('dashboard_channel_name') : 'NO<sub>2</sub>',
                fontSize: fontSize,
                value: this.getFormattedValue(no2),
                maxValue: Math.max(value, 361),
                unit: no2.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('no2', valueCategory.type, aqi),
                channelLastTimestamp: no2.get('channel_last_timestamp')
            });
        }

        // Check if there is a H2S channel
        var h2s = this.getChannel(103);
        if (h2s) {
            var value = h2s.get('channel_converted_value');
            // There are no EPA recommendations for H2S.
            // The following limits are estimated based on https://www.atsdr.cdc.gov/toxfaqs/tf.asp?id=388&tid=67#bookmark10
            // The following limits are in ppb
            var valueCategory = this.getMatchingValueCategory([
                { limit: [-Infinity, Infinity], type: 'good' },
            ], value, true);
            var state = this.advancedConfig ? this.getStateFromRange(h2s) : this.getStateForRegularThresholds('h2s', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && h2s.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: h2s.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? h2s.get('dashboard_channel_name') : h2s.get('channel_name'),
                abbr: this.useCustomLabels ? h2s.get('dashboard_channel_name') : 'H<sub>2</sub>S',
                fontSize: fontSize,
                value: this.getFormattedValue(h2s),
                maxValue: Math.max(value, 20001),
                unit: h2s.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('h2s', valueCategory.type),
                channelLastTimestamp: h2s.get('channel_last_timestamp')
            });
        }

        // Check if there is a SO2 channel
        var so2 = this.getChannel(106);
        if (so2) {
            var value = so2.get('channel_converted_value');
            // The following limits are in ppb
            var valueCategory = this.getMatchingValueCategory([
                { limit: [-Infinity, Infinity], type: 'good' },
            ], value, true);
            var aqi = this.getAirQualityIndex(
                this.EpaAQICategories[valueCategory.type].aqiLimits,
                valueCategory.limit,
                value
            );
            var state = this.advancedConfig ? this.getStateFromRange(so2) : this.getStateForRegularThresholds('so2', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && so2.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: so2.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? so2.get('dashboard_channel_name') : so2.get('channel_name'),
                abbr: this.useCustomLabels ? so2.get('dashboard_channel_name') : 'SO<sub>2</sub>',
                fontSize: fontSize,
                value: this.getFormattedValue(so2),
                maxValue: Math.max(value, 186),
                unit: so2.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('so2', valueCategory.type, aqi),
                channelLastTimestamp: so2.get('channel_last_timestamp')
            })
        }

        // Check if there is a ozone channel
        var ozone = this.getChannel(109);
        if (ozone) {
            var value = ozone.get('channel_converted_value');
            // The following limits are in ppb
            var valueCategory = this.getMatchingValueCategory([
                { limit: [-Infinity, Infinity], type: 'good' },
            ], value, true);
            var aqi = this.getAirQualityIndex(
                this.EpaAQICategories[valueCategory.type].aqiLimits,
                valueCategory.limit,
                value
            );
            var state = this.advancedConfig ? this.getStateFromRange(ozone) : this.getStateForRegularThresholds('o3', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && ozone.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: ozone.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? ozone.get('dashboard_channel_name') : ozone.get('channel_name'),
                abbr: this.useCustomLabels ? ozone.get('dashboard_channel_name') : 'O<sub>3</sub>',
                fontSize: fontSize,
                value: this.getFormattedValue(ozone),
                maxValue: Math.max(value, 86),
                unit: ozone.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('o3', valueCategory.type, aqi),
                channelLastTimestamp: ozone.get('channel_last_timestamp')
            });
        }

        // Check if there is a CO channel
        var co = this.getChannel(112);
        if (co) {
            var value = co.get('channel_converted_value');
            // The following limits are in ppm (just like our readings)
            var valueCategory = this.getMatchingValueCategory([
                { limit: [-Infinity, Infinity], type: 'good' },
            ], value, true);
            var aqi = this.getAirQualityIndex(
                this.EpaAQICategories[valueCategory.type].aqiLimits,
                valueCategory.limit,
                value
            );
            var state = this.advancedConfig ? this.getStateFromRange(co) : this.getStateForRegularThresholds('co', valueCategory.type);

            let fontSize = 100;
            if(this.useCustomLabels && co.get('dashboard_channel_name').length > 12) fontSize = 70;

            columns.push({
                id: co.get('dashboard_channel_id'),
                color: state.color,
                name: this.useCustomLabels ? co.get('dashboard_channel_name') : co.get('channel_name'),
                abbr: this.useCustomLabels ? co.get('dashboard_channel_name') : 'CO',
                fontSize: fontSize,
                value: this.getFormattedValue(co),
                maxValue: Math.max(value, 12.5),
                unit: co.get('channel_unit'),
                status: state.name,
                tooltip: this.getTooltip('co', valueCategory.type, aqi),
                channelLastTimestamp: co.get('channel_last_timestamp')
            });
        }

        // Get any non-IAQ channels added by user
        var nonIAQChannels = this.getNonIAQChannels();
        if (nonIAQChannels.length > 0) {
            _.each(nonIAQChannels, (channel) => {
                var value = channel.get('channel_converted_value');
                var state = this.getStateFromRange(channel);

                let fontSize = 100;
                if(this.useCustomLabels && channel.get('dashboard_channel_name').length > 12) fontSize = 70;

                columns.push({
                    id: channel.get('dashboard_channel_id'),
                    color: state.color,
                    name: this.useCustomLabels ? channel.get('dashboard_channel_name') : channel.get('channel_name'),
                    abbr: this.useCustomLabels ? channel.get('dashboard_channel_name') : channel.get('channel_name'),
                    fontSize: fontSize,
                    value: this.getFormattedValue(channel),
                    maxValue: Math.max(value, 100),
                    unit: channel.get('channel_unit'),
                    status: state.name,
                    tooltip: null,
                    channelLastTimestamp: channel.get('channel_last_timestamp')
                });
            })
        }

        // Filter and select/deselect channels based on the current dashboard filter
        columns = _.filter(columns, (c) => {
            var showChannel = !this.lastFilterValue || c.name.toLowerCase().indexOf(this.lastFilterValue) != -1;

            // Deselect Channels that are filtered
            if (!showChannel && _.includes(this.channelsSelected, c.id)) {
                this.sendChannelDeselectSignal(c.id);
            }

            // Re select channels that are not or no longer filtered
            if (showChannel && _.includes(this.channelsSelected, c.id)) {
                this.sendChannelSelectSignal(c.id);
            }

            return showChannel;
        });

        // Extract the last updated timestamp from widget channels update the .w-rows element
        const targetWidgetId = this.options.widgetId;
        // Get greatest timestamp from widget channels
        const lastSeenChannel = widgetChannels.reduce((lastTimestampChannel, currentChannel) => {
            const currentChannelTimestamp = currentChannel.get('channel_last_timestamp');
            const latestTimestamp = lastTimestampChannel.get('channel_last_timestamp');
            return currentChannelTimestamp > latestTimestamp ? currentChannel : lastTimestampChannel;
        },  widgetChannels[0]);
        const latestTimestamp = lastSeenChannel ? lastSeenChannel.get('channel_last_timestamp') : 0;
        
        // Get timestamp string to display in the widget
        const lastSeenStr = Utils.Time.getChannelLastSeenString(latestTimestamp);
        const lastSeenColor = Utils.Time.getChannelLastSeenColor(latestTimestamp);
        const isLastSeenStale = Utils.Time.isChannelLastSeenStale(latestTimestamp);

        // Extract the list of the channel IDs from columns now, before we
        // start removing channels from the list for processing.
        const renderedChannelIds = <number[]>_.map(columns, 'id');

        const self = this;

        if (columns.length < 1) {
            self.$el.addClass('hidden');
            return; // stop rendering the widget
        }

        // Update the HTML for the columns (channels) of this widget
        TaskQueue.shared.enqueue(function updateHtmlForWidget() {
            ////////////////////
            // SETUP

            self.$el.removeClass('hidden');
            self.$el.removeClass('widget-w-1');
            self.$el.removeClass('widget-w-2');
            self.$el.removeClass('widget-w-3');

            // Dinamically adjust widget width
            if(columns.length >= 10){
                self.$el.addClass('widget-w-4');
            } else if(columns.length >= 6){
                self.$el.addClass('widget-w-3');
            } else if (columns.length >= 3) {
                self.$el.addClass('widget-w-2');
            } else {
                self.$el.addClass('widget-w-1');
            }

            // We only need to render the template the first time or if the user added new channels
            var newChannelsToRender = false;
            // Check for new added channels
            for (let i = 0; i < columns.length; ++i) {
                var c = columns[i];
                var column = self.$('.column[data-channel-id="' + c.id + '"]');
                if (column.length == 0) {
                    newChannelsToRender = true;
                    break;
                }
            }
            // Check for deleted channels
            var renderedChannels = self.$('.iaq-content').find('.simple-widget-channel');
            if (!newChannelsToRender) newChannelsToRender = renderedChannels.length !== columns.length;

            // Add opacity to each column based on last seen timestamp
            _.each(columns, (column) => {
                column.channelLastSeenOpacity = Utils.Time.getChannelLastSeenOpacity(column.channelLastTimestamp);
            });

            let title = self.options.widget.getParamValue('LOCATION', '');
            if(!self.useCustomTitle && moduleLocation) title = moduleLocation;            
            if (self.firstRender || newChannelsToRender || self.triggeredBySidepanel) {
                self.$('.w-rows').html(self.channelsTemplate({
                    channelsSelected: self.channelsSelected,
                    columns: columns,
                    location: title,
                    time: lastSeenStr,
                    lastSeenColor: lastSeenColor,
                    isLastSeenStale: isLastSeenStale,
                }));
                self.firstRender = false;
            } else {
                // Update values on template
                for (let i = 0; i < columns.length; i++) {
                    var c = columns[i];

                    var channelColumn = self.$(`.column[data-channel-id='${c.id}']`);
                    var isClicked = channelColumn.hasClass('clicked');
                    var color = isClicked ? '#FFF' : c.color;

                    channelColumn.data('channel-status-color', _.escape(c.color));
                    channelColumn.find('.channel-name').html(c.abbr);
                    channelColumn.find('.channel-name').css('font-size', `${c.fontSize}%`);
                    channelColumn.find('.gauge-value').text(c.value.toString());
                    channelColumn.find('.status span').text(c.status);
                }
                self.$('.iaq-content .location').text(title);
            }

            (<any>self.$('.w-tooltip')).tooltip();

            ////////////////////
            // COLUMNS

            for (let i = 0; i < columns.length; ++i) {
                const c = columns[i];
                if (c == null || c.id == null || c.value == null) {
                    continue;
                }

                const column = self.$('[data-channel-id="' + c.id + '"]');

                // Compute the limits
                let min = 0;
                let max = Number(c.maxValue);
                const value = Number(c.value);

                // Compute 2.5% of the max value to use as an offset
                // This will create the gap in the gauges
                const offset = (max - min) * 2.5 / 100;

                // Ensure that the min and max have at least an offset difference from the value,
                // since we need that difference to create the gap for the gauge.
                if (value != null) {
                  min = Math.min(value, min);
                  max = Math.max(value, max);
                
                  // Adjust min if too close to value
                  if (value - min < offset) {
                    min = value - offset;
                  }
              
                  // Adjust max if too close to value
                  if (max - value < offset) {
                    max = value + offset;
                  }
                }

                // To create the gap and the appearance of two lines in the gauge, we use staticZones.
                // The gap is actually a transparent zone that spans -1.5/+1.5 points from the value.
                let staticZones = [
                  { strokeStyle: c.color, min: min, max: value - offset }, // state color for the fill
                  { strokeStyle: "#FFFFFF00", min: value - offset, max: value + offset }, // transparent gor gap
                  { strokeStyle: "#E5E7EB", min: value + offset, max: max }, // gray for empty
                ];
                // Handle empty/full gauge
                if (value === 0) {
                  staticZones = [
                    { strokeStyle: "#E5E7EB", min: min, max: max }, // gray for empty
                  ];
                }
                if (value === Number(c.maxValue)) {
                  staticZones = [
                    { strokeStyle: c.color, min: min, max: max }, // state color for full
                  ];
                }

                c.staticZones = staticZones;

                // If selected, apply clicked color transformation to staticZones
                const isSelected = column.hasClass('clicked');
                let zonesForGauge = c.staticZones;
                if (isSelected) {
                    zonesForGauge = c.staticZones.map(zone => {
                        if (zone.strokeStyle === c.color) {
                            // Zone 1 becomes Zone 3 color
                            return { ...zone, strokeStyle: "#E5E7EB" };
                        } else if (zone.strokeStyle === "#E5E7EB") {
                            // Zone 3 becomes Zone 1 color (match hover behavior)
                            return { ...zone, strokeStyle: "#0000001a" };
                        } else {
                            return zone;
                        }
                    });
                }

                const opts = {
                    angle: -0.3,
                    lineWidth: 0.06,
                    radiusScale: 1,
                    generateGradient: true,
                    pointer: {
                      length: 0,
                      strokeWidth: 0,
                    },
                    colorStart: isSelected ? '#FFF' : c.color,
                    strokeColor: isSelected ? c.color : "#e0e0e0",
                    defaultColor: c.color,
                    animationSpeed: 1,
                    defaultBackgroundColor: "#e0e0e0",
                    selected: isSelected,
                    staticZones: zonesForGauge,
                };

                // Create a new gauge for the channel, else use existing one
                const canvas = column.find('.canvas-gauge').get(0);
                if (self.gaugesByDashChannelId.size === 0 || 
                    !self.gaugesByDashChannelId.get(c.id) || 
                    newChannelsToRender || 
                    self.triggeredBySidepanel
                ) {
                    self.gaugesByDashChannelId.set(c.id, new Gauge(canvas));
                }
                const gauge = self.gaugesByDashChannelId.get(c.id);
                gauge.id = c.id;
                gauge.canvas = canvas;
                gauge.setOptions(opts);
                gauge.animationSpeed = 1;
                gauge.maxValue = c.maxValue;
                gauge.setMinValue(min || 0);
                gauge.set(c.value)
                gauge.update(true);

                // Store original zones for clearAllSelections reset
                column.data('originalStaticZones', c.staticZones);

                self.$('.column[data-channel-id="' + c.id + '"]').hover(function () {
                    // Don't apply hover effects if the gauge is already selected (clicked)
                    if (gauge.options.selected) {
                        return;
                    }
                    
                    // On hover: invert the static zones colors
                    // Zone 1 (c.color) becomes Zone 3 color (#E5E7EB)
                    // Zone 3 (#E5E7EB) becomes Zone 1 color (c.color)
                    // Zone 2 (transparent) stays transparent
                    
                    const originalStaticZones = c.staticZones;
                    const hoveredStaticZones = originalStaticZones.map(zone => {
                        if (zone.strokeStyle === c.color) {
                            // Zone 1 becomes Zone 3 color
                            return { ...zone, strokeStyle: "#E5E7EB" };
                        } else if (zone.strokeStyle === "#E5E7EB") {
                            return { ...zone, strokeStyle: "#0000001a" };
                        } else {
                            return zone;
                        }
                    });
                    
                    gauge.options.staticZones = hoveredStaticZones;
                    gauge.update(true);
                }, function () {
                    // Don't restore colors if the gauge is selected (clicked)
                    if (gauge.options.selected) {
                        return;
                    }
                    
                    // On mouse out: restore original static zones
                    gauge.options.staticZones = c.staticZones;
                    gauge.update(true);
                });

                self.$('.column[data-channel-id="' + c.id + '"]').click(function () {
                    if (!gauge.options.selected) {
                        // When selecting: invert the colors
                        const originalStaticZones = c.staticZones;
                        const selectedStaticZones = originalStaticZones.map(zone => {
                            if (zone.strokeStyle === c.color) {
                                return { ...zone, strokeStyle: "#E5E7EB" };
                            } else if (zone.strokeStyle === "#E5E7EB") {
                                return { ...zone, strokeStyle: "#0000001a" };
                            } else {
                                return zone;
                            }
                        });
                        
                        gauge.options.staticZones = selectedStaticZones;
                    } else {
                        // When deselecting: restore original colors
                        gauge.options.staticZones = c.staticZones;
                    }
                    
                    gauge.options.selected = !gauge.options.selected;
                    gauge.update(true);
                });

                self.$('*[data-channel-id="' + c.id + '"]').find('.gauge-value').text(c.value.toString());
            }

            ////////////////////
            // BOOKKEEPING

            // Re-flow the widgets in the category if the number of channels
            // being rendered changed.
            if (self.lastRenderedChannelIds.length !== renderedChannelIds.length
                || _.difference(self.lastRenderedChannelIds, renderedChannelIds).length !== 0) {
                window['App'].Bus.trigger('dashboard:category:' + self.options.categoryId + ':layout');
            }

            // Note which channels were rendered to allow future calls
            // to renderChannels() to determine if the number of
            // channels being rendered has changed.
            self.lastRenderedChannelIds = renderedChannelIds;

            self.triggeredBySidepanel = false;
            
            // Sync channelsSelected with channels currently in the default chart
            // This restores clicked state when switching tabs
            self.syncChannelsWithChart(() => {
                // Sync clicked class and gauge state with chart
                self.channelsSelected.forEach((channelId) => {
                    const gauge = self.gaugesByDashChannelId.get(channelId);
                    const column = self.$('[data-channel-id="' + channelId + '"]');
                    if (column.length) {
                        column.addClass('clicked');
                        if (gauge && !gauge.options.selected) {
                            const originalZones = column.data('originalStaticZones');
                            if (originalZones) {
                                const color = column.data('channel-status-color');
                                const selectedStaticZones = originalZones.map(zone => {
                                    if (zone.strokeStyle === color) {
                                        return { ...zone, strokeStyle: "#E5E7EB" };
                                    } else if (zone.strokeStyle === "#E5E7EB") {
                                        return { ...zone, strokeStyle: "#0000001a" };
                                    } else {
                                        return zone;
                                    }
                                });
                                gauge.options.staticZones = selectedStaticZones;
                            }
                            gauge.options.selected = true;
                            gauge.update(true);
                        }
                    }
                });

                // Deselect gauges no longer in channelsSelected
                self.gaugesByDashChannelId.forEach((gauge, channelId) => {
                    if (gauge.options.selected && !_.includes(self.channelsSelected, channelId)) {
                        const column = self.$('[data-channel-id="' + channelId + '"]');
                        const originalZones = column.data('originalStaticZones');
                        if (originalZones) {
                            gauge.options.staticZones = originalZones;
                        }
                        gauge.options.selected = false;
                        gauge.update(true);
                        if (column.length) {
                            column.removeClass('clicked');
                        }
                    }
                });
            });
        });
        
        // Apply fixed height equal to 2 simple widgets (each with 3 channels)
        const calculatedHeight = WIDGET_DIMENSIONS.getSimpleWidgetHeight(3);
        this.$('.body').css('height', calculatedHeight + 'px').addClass('w-iaq-height-constrained');
    }

    private getChannel(channel_order) {
        return this.options.dashboardChannelCollection.findWhere({
            dashboard_channel_order: channel_order,
            dashboard_widget_id: this.options.widgetId
        });
    }

    private getNonIAQChannels() {
        var nonIAQChannels = this.options.dashboardChannelCollection.where({
            dashboard_channel_order: 0,
            dashboard_widget_id: this.options.widgetId
        });
        var orderedNonIAQChannels = nonIAQChannels.sort((a, b) => {
            return a.get('dashboard_channel_id') - b.get('dashboard_channel_id');
        });
        return orderedNonIAQChannels;
    }

    private getFormattedValue(channel: DashboardChannel) {
        const value = Utils._getFormattedValue(
            channel.get('channel_converted_value'),
            channel.get('sensor_type'),
            channel.get('sensor_version'),
            Utils._getFormatDecimals(channel.get('channel_decimals'), channel.get('channel_converted_value')),
            channel.get('channel_conversion_function_name'));
        const formattedValue = Utils.validateFormattedValue(value);

        return formattedValue;
    }

    private getAirQualityIndex(aqiLimits: [number, number], valueLimits: [number, number], value: number) {
        var valuePercentage = (value - valueLimits[0]) / (valueLimits[1] - valueLimits[0]);
        return Math.max(0,
            Math.min(300,
                Math.round(aqiLimits[0] + (valuePercentage * (aqiLimits[1] - aqiLimits[0])))))
    }

    private getMatchingValueCategory(limits: ValueCategory[], value, mustMatch: boolean): ValueCategory {
        for (var i = 0; i < limits.length; i++) {
            if (limits[i].limit[0] == null || limits[i].limit[1] == null) continue;

            if (value >= limits[i].limit[0] && value < limits[i].limit[1]) {
                return limits[i];
            }
        }

        if (mustMatch) {
            if (value <= limits[0].limit[0]) return limits[0];
            if (value >= limits[limits.length - 1].limit[1]) return limits[limits.length - 1];
        }
    }

    private getStateForRegularThresholds(type: string, status: string, value?: number) {
        switch (type) {
            case 'temp':
                var defaultState = {
                    name: 'Comfortable',
                    color: Utils.colors.green
                }
                var tempChannel = this.getChannel(1);
                if (!tempChannel) return defaultState;
                var limits = this.getThresholdsLimitsFromRangesList(tempChannel.get('rangesList'));

                if (!_.isNumber(value)) {
                    return defaultState;
                } else if (limits.critHigh !== null && value >= limits.critHigh) {
                    return {
                        name: 'Hot',
                        color: Utils.colors.red
                    };
                } else if (limits.critLow !== null && value <= limits.critLow) {
                    return {
                        name: 'Cold',
                        color: Utils.colors.blue
                    };
                } else if (limits.warningHigh !== null && value >= limits.warningHigh) {
                    return {
                        name: 'Warm',
                        color: Utils.colors.yellow
                    };
                } else if (limits.warningLow !== null && value <= limits.warningLow) {
                    return {
                        name: 'Cool',
                        color: Utils.colors["light-blue"]
                    };
                } else {
                    return defaultState;
                }
            case 'hum':
                var defaultState = {
                    name: 'Comfortable',
                    color: Utils.colors.green
                }
                var humChannel = this.getChannel(2);
                var limits = this.getThresholdsLimitsFromRangesList(humChannel.get('rangesList'));
                if (!humChannel) return defaultState;

                if (!_.isNumber(value)) {
                    return defaultState;
                } else if (limits.critHigh !== null && value >= limits.critHigh) {
                    return {
                        name: 'Too Humid',
                        color: Utils.colors.red
                    };
                } else if (limits.critLow !== null && value <= limits.critLow) {
                    return {
                        name: 'Too Dry',
                        color: Utils.colors.red
                    };
                } else if (limits.warningHigh !== null && value >= limits.warningHigh) {
                    return {
                        name: 'Humid',
                        color: Utils.colors.yellow
                    };
                } else if (limits.warningLow !== null && value <= limits.warningLow) {
                    return {
                        name: 'Dry',
                        color: Utils.colors.yellow
                    };
                } else {
                    return defaultState;
                }
            case 'co2':
            case 'voc':
            case 'no2':
            case 'pm':
            case 'so2':
            case 'o3':
            case 'co':
            case 'hcho':
            case 'h2s':
                switch (status) {
                    case 'excellent':
                        return {
                            name: 'Excellent',
                            color: Utils.colors.green
                        };
                    case 'outdoor':
                    case 'good':
                        return {
                            name: 'Good',
                            color: Utils.colors.green
                        };
                    case 'moderate':
                        return {
                            name: 'Moderate',
                            color: Utils.colors.yellow
                        };
                    case 'poor':
                        return {
                            name: 'Poor',
                            color: Utils.colors.orange
                        };
                    case 'usg':
                        return {
                            name: 'USG',
                            color: Utils.colors.orange
                        };
                    case 'unhealthy':
                        return {
                            name: 'Unhealthy',
                            color: Utils.colors.red
                        };
                    case 'very-unhealthy':
                        return {
                            name: 'Very Unhealthy',
                            color: Utils.colors.purple
                        };
                    case 'hazardous':
                        return {
                            name: 'Hazardous',
                            color: Utils.colors.maroon
                        };
                }
                break;
        }
    }

    private getTooltip(type: string, status: string, aqi?: number) {
        // From https://www3.epa.gov/airnow/aqi-technical-assistance-document-may2016.pdf
        var aqiAddon = aqi != null ? '<br>Air Quality Index: ' + aqi : '';
        var sources = "<br><br>Air Quality standards and recommendations are sourced from the EPA, the Well Building Standard, and International Air Quality Standards";

        switch (type) {
            case 'temp':
                var temp = this.getChannel(1);
                var isCelcius = temp.get('channel_unit').toLowerCase() === '°c' || temp.get('channel_unit').toLowerCase() === 'c';
                var channelUnit = _.escape(temp.get('channel_unit'));
                var values = {
                    critLow: isCelcius ? 18 : 66,
                    warningLow: isCelcius ? 20 : 68,
                    warningHigh: isCelcius ? 23 : 74,
                    critHigh: isCelcius ? 25 : 77,
                }
                switch (status) {
                    case 'crit-low':
                        return '<b>Cold</b><br>' +
                            `Temperatures below ${values.critLow}${channelUnit} are considered cold.`;
                    case 'warn-low':
                        return '<b>Cool</b><br>' +
                            `Temperatures between ${values.critLow}${channelUnit} and ${values.warningLow}${channelUnit} are considered cool.`;
                    case 'good':
                        return '<b>Comfortable</b><br>' +
                            `Temperatures between ${values.warningLow}${channelUnit} and ${values.warningHigh}${channelUnit} are considered ideal.`;
                    case 'warn-high':
                        return '<b>Warm</b><br>' +
                            `Temperatures between ${values.warningHigh}${channelUnit} and ${values.critHigh}${channelUnit} are considered warm.`;
                    case 'crit-high':
                        return '<b>Hot</b><br>' +
                            `Temperatures above ${values.critHigh}${channelUnit} are considered hot.`;
                }

            case 'hum':
                var hum = this.getChannel(2);
                var channelUnit = _.escape(hum.get('channel_unit'));
                switch (status) {
                    case 'crit-low':
                        return '<b>Too Dry</b><br>' +
                            'Humidity below 20' + channelUnit + ' is considered too dry for comfort.';
                    case 'warn-low':
                        return '<b>Dry</b><br>' +
                            'Humidity between 20' + channelUnit + ' and 30' + channelUnit + ' is considered dry.';
                    case 'good':
                        return '<b>Comfortable</b><br>' +
                            'Humidity between 30' + channelUnit + ' and 70' + channelUnit + ' is considered ideal.';
                    case 'warn-high':
                        return '<b>Humid</b><br>' +
                            'Humidity between 70' + channelUnit + ' and 80' + channelUnit + ' is considered humid.';
                    case 'crit-high':
                        return '<b>Too Humid</b><br>' +
                            'Humidity above 80' + channelUnit + ' is considered too humid for comfort.';
                }
                break;

            case 'co2':
                switch (status) {
                    case 'outdoor':
                        return '<b>Good</b><br>' +
                            'CO<sub>2</sub> concentrations below 350ppm are considered the normal background concentration in outdoor ambient air.' + sources;
                    case 'good':
                        return '<b>Good</b><br>' +
                            'CO<sub>2</sub> concentrations below 1000ppm are considered typical of occupied indoor spaces with good air exchange.' + sources;
                    case 'moderate':
                        return '<b>Moderate</b><br>' +
                            'CO<sub>2</sub> concentrations between 1000ppm and 2000pm are considered moderate. Expect complaints of drowsiness and poor air.' + sources;
                    case 'unhealthy':
                        return '<b>Unhealthy</b><br>' +
                            'CO<sub>2</sub> concentrations between 2000ppm and 5000pm are considered unhealthy. Expect headaches, sleepiness and stagnant, stale, stuffy air. Poor concentration, loss of attention, increased heart rate and slight nausea may also be present.' + sources;
                    case 'very-unhealthy':
                        return '<b>Very Unhealthy</b><br>' +
                            'CO<sub>2</sub> concentrations between 5000ppm and 40000pm are considered very unhealthy. This is the workplace exposure limit in most jurisdictions.' + sources;
                    case 'hazardous':
                        return '<b>Hazardous</b><br>' +
                            'CO<sub>2</sub> concentrations above 40000ppm are considered hazardous. Exposure may lead to serious oxygen deprivation resulting in permanent brain damage, coma, even death.' + sources;
                }
                break;

            case 'voc':
                switch (status) {
                    case 'excellent':
                        return '<b>Excellent</b><br>' +
                            'TVOC concentrations below 65ppb are excellent.' + sources;
                    case 'good':
                        return '<b>Good</b><br>' +
                            'TVOC concentrations between 65ppb and 220ppb are good.' + sources;
                    case 'moderate':
                        return '<b>Moderate</b><br>' +
                            'TVOC concentrations between 220ppb and 660ppb are moderate. Exposure < 12 months. ' + sources;
                    case 'poor':
                        return '<b>Poor</b><br>' +
                            'TVOC concentrations above 660ppb are considered poor. Exposure < 1 month. ' + sources;
                    case 'unhealthy':
                        return '<b>Unhealthy</b><br>' +
                            'TVOC concentrations above 2200ppb are considered unhealthy. Use only if unavoidable. Exposure < hours. ' + sources;
                }
                break;

            case 'pm':
                switch (status) {
                    case 'good':
                        return '<b>Good' + aqiAddon + '</b><br>' +
                            'The particulate matter concentration is normal.' + sources;
                    case 'moderate':
                        return '<b>Moderate' + aqiAddon + '</b><br>' +
                            'Respiratory symptoms possible in unusually sensitive individuals; possible aggravation of heart or lung disease in people with cardiopulmonary disease and older adults.' + sources;
                    case 'usg':
                        return '<b>Unhealthy for Sensitive Groups' + aqiAddon + '</b><br>' +
                            'Increasing likelihood of respiratory symptoms in sensitive groups including older adults, children, and people of lower socioeconomic status; aggravation of heart or lung disease and premature mortality in people with heart or lung disease' + sources;
                    case 'unhealthy':
                        return '<b>Unhealthy' + aqiAddon + '</b><br>' +
                            'Increased aggravation of respiratory symptoms in sensitive groups including older adults, children, and people of lower socioeconomic status; increased aggravation of heart or lung disease and premature mortality in people with heart or lung disease; increased respiratory effects in general population.' + sources;
                    case 'very-unhealthy':
                        return '<b>Very Unhealthy' + aqiAddon + '</b><br>' +
                            'Significant aggravation of respiratory symptoms in sensitive groups including older adults, children, and people of lower socioeconomic status; significant aggravation of heart or lung disease and premature mortality in people with heart or lung disease; significant increase in respiratory effects in general population.' + sources;
                    case 'hazardous':
                        return '<b>Hazardous' + aqiAddon + '</b><br>' +
                            'Serious aggravation of respiratory symptoms in sensitive groups including older adults, children, and people of lower socioeconomic status; serious aggravation of heart or lung disease and premature mortality in people with heart or lung disease; serious risk of respiratory effects in general population' + sources;
                }
                break;

            case 'hcho':
                switch (status) {
                    case 'good':
                        return '<b>Good</b><br>' +
                            'The formaldehyde concentration is normal.' + sources;
                    case 'moderate':
                        return '<b>Moderate</b><br>' +
                            'According to the Well Building Standard formaldehyde concentrations between 27ppb and 81ppb are considered moderate';
                    case 'unhealthy':
                        return '<b>Unhealthy</b><br>' +
                            'According to the OSHA Formaldehyde standard Airborne concentrations of formaldehyde above 0.1 ppm can cause irritation of the respiratory tract. The severity of irritation intensifies as concentrations increase.';
                    case 'very-unhealthy':
                        return '<b>Very Unhealthy</b><br>' +
                            'The OSHA Formaldehyde standard includes a permissible exposure limit of 0.75 ppm measured as an 8-hour time-weighted average';
                    case 'hazardous':
                        return '<b>Hazardous</b><br>' +
                            'The OSHA Formaldehyde standard includes a permissible exposure limit in the form of a short-term exposure limit (STEL) of 2 ppm which is the maximum exposure allowed during a 15-minute period';
                }
                break;

            case 'no2':
                switch (status) {
                    case 'good':
                        return '<b>Good' + aqiAddon + '</b><br>' +
                            'The nitrogen dioxide concentration is normal.' + sources;
                    case 'moderate':
                        return '<b>Moderate' + aqiAddon + '</b><br>' +
                            'The nitrogen dioxide concentration is moderate.' + sources;
                    case 'usg':
                        return '<b>Unhealthy for Sensitive Groups' + aqiAddon + '</b><br>' +
                            'Increasing likelihood of respiratory symptoms, such as chest tightness and breathing discomfort, in people with asthma.' + sources;
                    case 'unhealthy':
                        return '<b>Unhealthy' + aqiAddon + '</b><br>' +
                            'Significant increase in respiratory symptoms, such as wheezing and shortness of breath, in people with asthma; aggravation of other lung diseases.' + sources;
                    case 'very-unhealthy':
                        return '<b>Very Unhealthy' + aqiAddon + '</b><br>' +
                            'Increased respiratory symptoms, such as chest tightness and wheezing in people with asthma; possible aggravation of other lung diseases.' + sources;
                    case 'hazardous':
                        return '<b>Hazardous' + aqiAddon + '</b><br>' +
                            'Severe respiratory symptoms, such as wheezing and shortness of breath, in people with asthma; increased aggravation of other lung diseases; possible respiratory effects in general population.' + sources;
                }
                break;

            case 'h2s':
                switch (status) {
                    case 'good':
                        return '<b>Good</b><br>' +
                            'According to National Institute for Occupational Safety and Health (NIOSH) a concentration below 10,000ppb is considered good';
                    case 'moderate':
                        return '<b>Moderate</b><br>' +
                            'A value between 10,000ppb and 20,000ppb is considered moderate. The Occupational Safety and Health Administration (OSHA) set an acceptable ceiling limit of 20 ppm for hydrogen sulfide in workplace air.';
                    case 'unhealthy':
                        return '<b>Unhealthy</b><br>' +
                            'A value between 20,000ppb and 50,000ppb is considered unhealthy. This value is above the acceptable ceiling limit for workspace air set by the Occupational Safety and Health Administration (OSHA).';
                    case 'very-unhealthy':
                        return '<b>Very Unhealthy</b><br>' +
                            'A value between 50,000ppb and 80,000ppb is considered very unhealthy. This value is above the acceptable ceiling limit for workspace air set by the Occupational Safety and Health Administration (OSHA).';
                    case 'hazardous':
                        return '<b>Hazardous</b><br>' +
                            'A above 80,000ppb is immediately dangerous to life or health of workers.';
                }
                break;

            case 'so2':
                switch (status) {
                    case 'good':
                        return '<b>Good' + aqiAddon + '</b><br>' +
                            'The sulfur dioxide concentration is normal.' + sources;
                    case 'moderate':
                        return '<b>Moderate' + aqiAddon + '</b><br>' +
                            'The sulfur dioxide concentration is moderate.' + sources;
                    case 'usg':
                        return '<b>Unhealthy for Sensitive Groups' + aqiAddon + '</b><br>' +
                            'Increasing likelihood of respiratory symptoms, such as chest tightness and breathing discomfort, in people with asthma.' + sources;
                    case 'unhealthy':
                        return '<b>Unhealthy' + aqiAddon + '</b><br>' +
                            'Increased respiratory symptoms, such as chest tightness and wheezing in people with asthma; possible aggravation of other lung diseases.' + sources;
                    case 'very-unhealthy':
                        return '<b>Very Unhealth' + aqiAddon + 'y</b><br>' +
                            'Significant increase in respiratory symptoms, such as wheezing and shortness of breath, in people with asthma; aggravation of other lung diseases.' + sources;
                    case 'hazardous':
                        return '<b>Hazardous' + aqiAddon + '</b><br>' +
                            'Severe respiratory symptoms, such as wheezing and shortness of breath, in people with asthma; increased aggravation of other lung diseases; possible respiratory effects in general population.' + sources;
                }
                break;

            case 'o3':
                switch (status) {
                    case 'good':
                        return '<b>Good' + aqiAddon + '</b><br>' +
                            'The ozone concentration is normal.' + sources;
                    case 'moderate':
                        return '<b>Moderate' + aqiAddon + '</b><br>' +
                            'Unusually sensitive individuals may experience respiratory symptoms.' + sources;
                    case 'usg':
                        return '<b>Unhealthy for Sensitive Groups' + aqiAddon + '</b><br>' +
                            'Increasing likelihood of respiratory symptoms and breathing discomfort in people with lung disease (such as asthma), children, older adults, people who are active outdoors (including outdoor workers), people with certain genetic variants, and people with diets limited in certain nutrients.' + sources;
                    case 'unhealthy':
                        return '<b>Unhealthy' + aqiAddon + '</b><br>' +
                            'Greater likelihood of respiratory symptoms and breathing in people with lung disease (such as asthma), children, older adults, people who are active outdoors (including outdoor workers), people with certain genetic variants, and people with diets limited in certain nutrients; possible respiratory effects in general population.' + sources;
                    case 'very-unhealthy':
                        return '<b>Very Unhealthy' + aqiAddon + '</b><br>' +
                            'Increasingly severe symptoms and impaired breathing likely in people with lung disease (such as asthma), children, older adults, people who are active outdoors (including outdoor workers), people with certain genetic variants, and people with diets limited in certain nutrients; increasing likelihood of respiratory effects in general population.' + sources;
                    case 'hazardous':
                        return '<b>Hazardous' + aqiAddon + '</b><br>' +
                            'Severe respiratory effects and impaired breathing likely in people with lung disease (such as asthma), children, older adults, people who are active outdoors (including outdoor workers), people with certain genetic variants, and people with diets limited in certain nutrients; increasingly severe respiratory effects likely in general population.' + sources;
                }
                break;

            case 'co':
                switch (status) {
                    case 'good':
                        return '<b>Good' + aqiAddon + '</b><br>' +
                            'The carbon monoxide concentration is normal.' + sources;
                    case 'moderate':
                        return '<b>Moderate' + aqiAddon + '</b><br>' +
                            'The carbon monoxide concentration is moderate.' + sources;
                    case 'usg':
                        return '<b>Unhealthy for Sensitive Groups' + aqiAddon + '</b><br>' +
                            'Increasing likelihood of reduced exercise tolerance due to increased cardiovascular symptoms, such as chest pain, in people with heart disease.' + sources;
                    case 'unhealthy':
                        return '<b>Unhealthy' + aqiAddon + '</b><br>' +
                            'Reduced exercise tolerance due to increased cardiovascular symptoms, such as chest pain, in people with heart disease.' + sources;
                    case 'very-unhealthy':
                        return '<b>Very Unhealthy' + aqiAddon + '</b><br>' +
                            'Significant aggravation of cardiovascular symptoms, such as chest pain, in people with heart disease.' + sources;
                    case 'hazardous':
                        return '<b>Hazardous' + aqiAddon + '</b><br>' +
                            'Serious aggravation of cardiovascular symptoms, such as chest pain, in people with heart disease; impairment of strenuous activities in general population.' + sources;
                }
                break;
        }
    }

    protected clearAllSelections() {
        // Reset gauge canvas state before the base class clears selections
        this.gaugesByDashChannelId.forEach((gauge: any, channelId) => {
            if (gauge && gauge.options.selected) {
                const column = this.$('[data-channel-id="' + channelId + '"]');
                const originalZones = column.data('originalStaticZones');
                if (originalZones) {
                    gauge.options.staticZones = originalZones;
                }
                gauge.options.selected = false;
                gauge.update(true);
            }
        });
        super.clearAllSelections();
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/iaq/IAQWidget.ts